'use client';

import Image from 'next/image';
import type { MentorshipProgramDetail } from '@/shared/job-mentorship';
import { mentorshipApplyClosedLabel } from '@/features/mentorship';
import { useMentorshipRegistration } from './hooks/useMentorshipRegistration';
import MentorCVDialog from './MentorCVDialog';
import MentorThankfulDialog from './MentorThankfulDialog';
import ApiMentorshipSurveyDialog from './ApiMentorshipSurveyDialog';

type Props = {
  programId: number;
  jobId: number;
  canApply: boolean;
  applyClosedReason?: MentorshipProgramDetail['applyClosedReason'];
  questions: MentorshipProgramDetail['questions'];
};

export default function MentorRegisterButton({
  programId,
  jobId,
  canApply,
  applyClosedReason,
  questions,
}: Props) {
  const reg = useMentorshipRegistration({
    programId,
    jobId,
    canApply,
    questions,
  });

  return (
    <>
      <div
        className="flex flex-col items-center"
        data-program-id={String(programId)}
      >
        <button
          type="button"
          onClick={() => reg.setCvOpen(true)}
          disabled={!reg.registrationAllowed}
          title={
            reg.registrationAllowed
              ? undefined
              : mentorshipApplyClosedLabel(applyClosedReason)
          }
          className={`inline-flex items-center gap-1.5 px-6 py-2 rounded-lg text-black font-normal text-[16px] leading-6 tracking-[-0.64px] w-40.5 h-10 ${
            reg.registrationAllowed
              ? 'cursor-pointer opacity-100'
              : 'opacity-50 cursor-not-allowed'
          }`}
          style={{
            background: 'linear-gradient(90deg, #0AC1CE 0%, #B4FAFF 100%)',
            boxShadow: '0px 2px 4px -2px #0A0D120F, 0px 4px 8px -2px #0A0D121A',
          }}
        >
          <Image
            src="/mentor/mentor-register.svg"
            alt=""
            width={16}
            height={16}
          />
          Бүртгүүлэх
        </button>
        {!reg.registrationAllowed && (
          <p className="mt-1.5 text-sm text-red-500 font-medium text-center">
            {mentorshipApplyClosedLabel(applyClosedReason)}
          </p>
        )}
      </div>

      <MentorCVDialog
        isOpen={reg.cvOpen}
        onClose={() => reg.setCvOpen(false)}
        user={reg.user ?? null}
        onLoginRedirect={reg.onLogin}
        onEditCv={reg.onEditCv}
        onSendCv={() => void reg.onAfterCvSend()}
        isSubmitting={reg.busy}
      />

      <MentorThankfulDialog
        isOpen={reg.thanksOpen}
        onClose={() => reg.setThanksOpen(false)}
      />

      <ApiMentorshipSurveyDialog
        isOpen={reg.surveyOpen}
        onClose={() => reg.setSurveyOpen(false)}
        programId={programId}
        applyId={reg.applyId}
        questions={questions}
        onSuccess={() => reg.setThanksOpen(true)}
      />
    </>
  );
}
